import React, { Component } from "react";
import PropTypes from "prop-types";

import clsx from "clsx";
import i18n from "i18n-react";

import { capitalize } from "helpers/helpers";

const defaultTransitions = {
  width: "200ms",
  height: "200ms",
  borderRadius: "1000ms",
};

export default class PointsDisplay extends Component {
  constructor(props) {
    super(props);

    const { points, tiers, labels } = this.props;

    const ranks = {};
    tiers.forEach((tier, i) => {
      ranks[tier] = labels[i];
    });

    this.state = {
      tiers,
      progress: null,
      points,
      ranks,
    };
  }

  componentDidMount() {
    this.calculateProgress();
  }

  componentDidUpdate(prevProps, prevState) {
    this.recalculateValues(prevProps, prevState);
  }

  getTieredValue(array = null, pointsOverride = null) {
    const { tiers, points: pointsProp } = this.props;
    let index = 0;
    tiers.forEach((points, i) => {
      // Could be improved, maybe using filter / reduce?
      if ((pointsOverride || pointsProp) > points && index !== i) {
        index = i;
      }
    });
    return array ? array[index < 0 ? 0 : index] : index;
  }

  getStyleObject() {
    const { tierColors, textColors } = this.props;
    const style = {};

    const backgroundColor =
      tierColors.length > 0 && this.getTieredValue(tierColors);
    const color = textColors.length > 0 && this.getTieredValue(textColors);

    if (backgroundColor) style.backgroundColor = backgroundColor;
    if (color) style.color = color;

    return style;
  }

  recalculateValues(prevProps, prevState) {
    const { points } = this.props;
    const { points: pointState } = this.state;
    const { points: prevPointsProp } = prevProps;
    const { points: prevPointsState } = prevState;

    const changed = prevPointsProp !== points || prevPointsState !== points;
    if (changed && pointState !== points) {
      this.setState({ points }, () => {
        this.calculateProgress(points);
      });
    }
  }

  /**
   * Calculates the progress (0-100) based on the number of points the user has, within a predefined set of tiers.
   * Progress through these tiers is normalized, meaning each tier accounts for an equal portion of the total 100%
   * progress, regardless of the points' range it covers. This segmented approach ensures that progress is not linear
   * but adjusted according to the current tier the points fall into, allowing for a uniform distribution of progress
   * across different segments of achievement.
   *
   * @param {number} newPoints Calculate process based on a new parameter instead of the passed prop.
   */
  calculateProgress(newPoints = 0) {
    const { points: oldPoints, tiers } = this.props;
    const points = newPoints || oldPoints;

    // Catch edge cases where (somehow) points equal 0 or less (which is no progress made), or if no tiers have been set (which means there is no threshold to reach);
    if (points <= 0 || tiers?.length === 0) {
      this.setState({
        progress: 0,
      });
      return;
    }

    // Catch cases where the user has reached (more than) the maximum number of points.
    if (points >= tiers[tiers.length - 1]) {
      this.setState({
        progress: 100,
      });
      return;
    }

    // Calculate the percentage each tier/segment spans.
    const segmentSize = 100 / tiers.length;

    // Calculate the tier that is currently being worked on (based on the points the user has).
    let currentTierIndex = 0;
    tiers.forEach((tier, i) => {
      if (points >= tier) {
        currentTierIndex = i + 1;
      }
    });

    // Calculate the lower and upper bounds of the current tier (compared to the previously reached tier). (so for tiers: [100, 200, 300], the lower bound of the second tier is 100 and the upper bound is 200)
    const lowerBound = currentTierIndex === 0 ? 0 : tiers[currentTierIndex - 1];
    const upperBound = tiers[currentTierIndex];

    // Calculate the percentage of progress made within the current tier (remove lower bound to only look at the 'relevant' points)
    const progressInTier = (points - lowerBound) / (upperBound - lowerBound);

    // Calculate the overall progress. Since index is 0-based, we can multiply the current tier index by the segment size to get the progress made in the segments before the current one.
    const newProgress =
      currentTierIndex * segmentSize + progressInTier * segmentSize;

    this.setState({
      progress: newProgress,
    });

    // ------------------- OLD CODE -------------------
    // TODO: Reworked the code below to fix incorrect progress calculation.
    // Can be removed after current implementation is tested and approved.

    // console.log('CalculatePoints Test Results:', {
    //   points,
    //   tiers,
    //   currentTierIndex,
    //   lowerBound,
    //   upperBound,
    //   progressInTier,
    //   newProgress,
    // });

    // // Will be set to the new progress (meaning a score of 0 to 100)
    // let newProgress = 0;

    // // represents the NUMBER of the current tier (not the index!)
    // let current = 0;

    // // Calculate till closest tier based on points
    // tiers.forEach((tier, i) => {
    //   if (points >= tier) {
    //     current = i;
    //   }
    // });

    // current = current < 0 ? 0 : current + 1;
    // newProgress = (current / tiers.length) * 100;

    // if (current === tiers.length) {
    //   this.setState({
    //     progress: 100,
    //   });
    //   return;
    // }

    // // Calculate progress between tiers
    // const currentTier = tiers[current - 1];
    // const nextTier = tiers[current];
    // const pointsBetweenTiers = nextTier - currentTier;
    // const pointsToNextTier = points - currentTier;
    // const progressBetweenTiers = pointsToNextTier / pointsBetweenTiers;
    // const percentageBetweenTiers = progressBetweenTiers;
    // newProgress += (100 / tiers.length) * percentageBetweenTiers;

    // if (newProgress <= 8.5 || points === 0) {
    //   newProgress = 0;
    // }

    // this.setState({
    //   progress: newProgress,
    // });
  }

  render() {
    const {
      // tiers,
      segmented,
      orientation,
      transitions,
      startingValue,
      minSize,
    } = this.props;
    const { points: pointState, ranks, tiers } = this.state;
    const { progress } = this.state;

    // TODO: Commented out unused code [nextTier/currentTier] during Translations update. Remove during future cleanup if no longer needed.
    // const currentTier = tiers && this.getTieredValue();
    // const nextTier = tiers && (currentTier + 1 < tiers.length ? currentTier + 1 : tiers.length - 1);

    const mergedTransitions = { ...defaultTransitions, ...transitions };

    const transition = {
      transition: Object.keys(mergedTransitions)
        .map(
          (key) =>
            `${key.replace(/([A-Z])/g, "-$1").toLowerCase()} ${
              mergedTransitions[key]
            }`
        )
        .join(", "),
    };

    const key = orientation === "horizontal" ? "width" : "height";
    const wrapper = document.querySelector(".wrapper__bar");
    const value =
      +wrapper && wrapper[`client${capitalize(key)}`] / tiers.length;

    const overlayStyle = {
      [key]: `${
        progress
          ? Math.floor(progress)
          : Math.floor(
              (startingValue / (segmented ? tiers[tiers.length - 1] : 1)) *
                (segmented ? 100 : 1)
            )
      }%`,
    };

    const wrapperStyle = minSize ? { [`min${capitalize(key)}`]: minSize } : {};
    const points =
      pointState >= tiers[tiers.length - 1]
        ? tiers[tiers.length - 1]
        : pointState;
    const styleObject = this.getStyleObject();

    return (
      <div
        className={clsx("wrapper__bar", {
          horizontal: orientation === "horizontal",
          vertical: orientation === "vertical",
        })}
        style={{ ...wrapperStyle, ...transition }}
      >
        {segmented &&
          tiers.map((tier, i) => (
            <div
              key={tier}
              className={clsx("progress__bar", orientation, {
                start: i === 0,
                end: i === tiers.length - 1,
              })}
              style={styleObject}
            >
              <span>{ranks[tier]}</span>
            </div>
          ))}
        {!segmented && (
          <div className="progress__bar full" style={styleObject}>
            <span className={clsx("progress__info", orientation)}>
              <span>{i18n.translate("points.outOf", { points })}</span>
            </span>
          </div>
        )}
        <div
          className={clsx("progress__overlay", orientation, {
            end: Math.floor(progress) >= 95,
          })}
          style={{ ...overlayStyle, ...transition }}
        >
          {segmented &&
            tiers.map((tier) => (
              <span key={tier} style={{ flex: `0 0 ${value}px` }} />
            ))}
          {!segmented && (
            <span
              className={clsx("progress__info full", orientation)}
              style={{ flex: `0 0 ${value * tiers.length}px` }}
            />
          )}
        </div>
      </div>
    );
  }
}

PointsDisplay.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string),
  minSize: PropTypes.string,
  orientation: PropTypes.string,
  points: PropTypes.number,
  segmented: PropTypes.bool,
  startingValue: PropTypes.number,
  textColors: PropTypes.arrayOf(PropTypes.string),
  tierColors: PropTypes.arrayOf(PropTypes.string),
  tiers: PropTypes.arrayOf(PropTypes.number),
  transitions: PropTypes.shape(),
};

PointsDisplay.defaultProps = {
  labels: [],
  minSize: null,
  orientation: "horizontal",
  points: 0,
  segmented: false,
  startingValue: 0,
  textColors: [],
  tierColors: [],
  tiers: [0],
  transitions: defaultTransitions,
};
